import { useCallback, useEffect, useRef, useState } from "react";

import { Loader2, Search, X } from "lucide-react";

import { Button } from "js/components/ui/button";
import { Input } from "js/components/ui/input";
import { cn } from "lib/utils";

interface TableSearchProps {
  style?: Record<string, string>;
  onSearchSubmit?: (value: string) => void;
  onSearch?: (value: string) => void;
  onClear?: () => void;
  label: string;
  screen?: any;
  /**
   * Los DATOS que se filtran. Cuando cambian, se reaplica el termino activo.
   *
   * Hace falta porque el arreglo del bucle (MB-APP-EXPRESS-15K/-172) saco la
   * identidad de los callbacks de las deps del efecto: `onSearch` cierra sobre
   * los datos del padre, asi que al llegar o revalidarse la query el callback
   * nuevo solo actualizaba `callbacksRef` y NADIE volvia a invocar la busqueda.
   * La lista conservaba resultados viejos: si escribias durante la carga podia
   * quedar vacia aunque luego llegaran coincidencias, y tras un refetch omitia
   * los nuevos hasta que el usuario volviera a teclear.
   *
   * DEBE ser una referencia ESTABLE entre renders (el `data` de react-query lo
   * es). Pasar un literal fresco en cada render reintroduce el bucle que este
   * componente existe para evitar.
   */
  reapplyOn?: unknown;
}

const DEBOUNCE_MS = 350;

const TableSearch: React.FC<TableSearchProps> = ({
  onSearchSubmit,
  onSearch,
  onClear,
  style,
  label,
  reapplyOn,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Support both onSearchSubmit and onSearch prop names
  const searchHandler = onSearchSubmit || onSearch;

  /**
   * Ref "latest": guarda los callbacks del padre FUERA de las dependencias del
   * efecto de debounce.
   *
   * Por que: muchos padres pasan arrows inline (`onClear={() => ...}`), asi que
   * la identidad del prop cambia en CADA render del padre. Si esa identidad
   * esta en las deps del useEffect, cualquier re-render del padre relanza el
   * efecto; y como el efecto llamaba a `onClear()`, si el cuerpo de ese
   * `onClear` escribia estado no idempotente (p. ej. `setFilteredData(data || [])`,
   * que crea un array nuevo cada vez) el ciclo se cerraba:
   * efecto -> onClear -> setState -> render del padre -> nuevo onClear -> efecto.
   * Resultado: bucle infinito y React error 185 "Maximum update depth exceeded"
   * (MB-APP-EXPRESS-172 / -15K, en /tenants).
   *
   * Esto NO es "recortar una dep para callar el warning": la unica entrada
   * semanticamente relevante de este efecto es el TEXTO TECLEADO. Que el padre
   * recree su callback no es un evento de busqueda y no debe relanzar nada.
   * Y como el efecto siempre lee `callbacksRef.current`, se invoca SIEMPRE el
   * callback mas reciente: no se congela ningun closure viejo.
   */
  const callbacksRef = useRef({ searchHandler, onClear });

  // Debe declararse ANTES del efecto de debounce: los efectos corren en orden
  // de declaracion, asi el ref ya esta fresco cuando el de debounce se ejecuta.
  useEffect(() => {
    callbacksRef.current = { searchHandler, onClear };
  });

  /**
   * Marca si el input llego a tener texto. Sirve para emitir `onClear()` solo
   * en la transicion real no-vacio -> vacio, y NO en el montaje (el estado
   * inicial de `searchValue` es "", y emitir el clear ahi era un efecto
   * secundario espurio: es el disparo que arrancaba el bucle).
   */
  const hadValueRef = useRef(false);

  const dispatchSearch = useCallback((term: string) => {
    const { searchHandler: handler, onClear: clear } = callbacksRef.current;
    if (!handler) return;
    const trimmed = term.trim();
    if (!trimmed) {
      if (hadValueRef.current) {
        hadValueRef.current = false;
        clear?.();
      }
      return;
    }
    handler(trimmed);
  }, []);

  const handleClear = useCallback(() => {
    setSearchValue("");
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    setIsTyping(false);
    // Se marca antes de emitir para que el efecto de debounce, que corre
    // despues con searchValue === "", no vuelva a emitir el mismo clear.
    hadValueRef.current = false;
    callbacksRef.current.onClear?.();
  }, []);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    setIsTyping(false);
    dispatchSearch(searchValue);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      setIsTyping(false);
      dispatchSearch(searchValue);
    }
  };

  const handleChange = (value: string) => {
    setSearchValue(value);
    setIsTyping(true);
  };

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    const trimmed = searchValue.trim();

    if (!trimmed) {
      setIsTyping(false);
      // Solo en la transicion real no-vacio -> vacio. En el montaje no entra.
      if (hadValueRef.current) {
        hadValueRef.current = false;
        callbacksRef.current.onClear?.();
      }
      return undefined;
    }

    hadValueRef.current = true;

    debounceRef.current = setTimeout(() => {
      dispatchSearch(searchValue);
      setIsTyping(false);
      debounceRef.current = null;
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
    // `dispatchSearch` es estable (useCallback con deps []), asi que en
    // practica la unica dep viva es `searchValue`, el texto tecleado.
  }, [searchValue, dispatchSearch]);

  /**
   * Reaplica el termino ACTIVO cuando cambian los datos filtrados.
   *
   * Va en un efecto aparte a proposito: el de arriba es el debounce del tecleo
   * y es el que causaba el bucle, asi que no se toca. Este depende SOLO de
   * `reapplyOn`, nunca de la identidad de los callbacks, que es lo que hacia
   * que un padre con un callback inline re-renderizara sin fin.
   *
   * No puede ciclar: reaplicar escribe la lista FILTRADA del padre, no sus
   * datos, asi que `reapplyOn` no cambia por efecto de esta llamada.
   */
  const skipFirstReapplyRef = useRef(true);
  useEffect(() => {
    if (skipFirstReapplyRef.current) {
      // Montaje: el padre ya pinta sus datos completos, no hay nada que reaplicar.
      skipFirstReapplyRef.current = false;
      return;
    }
    const trimmed = searchValue.trim();
    // Sin termino activo no se toca nada: el camino de limpiado sigue siendo
    // exclusivo de la transicion no-vacio -> vacio (ver `hadValueRef`).
    if (!trimmed) return;
    // El usuario esta tecleando: manda el debounce, que ya va a disparar.
    if (debounceRef.current) return;
    dispatchSearch(trimmed);
    // `searchValue` se lee del render en el que cambio `reapplyOn`, que es el
    // actual: incluirlo en las deps duplicaria el efecto del debounce.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reapplyOn, dispatchSearch]);

  useEffect(() => () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
  }, []);

  const isDisabled = !searchValue.trim();

  return (
    <form onSubmit={handleSubmit} style={style} className="w-full">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder={`Buscar ${label ?? ""}`}
            value={searchValue}
            onChange={(event) => handleChange(event.target.value)}
            onKeyDown={handleKeyDown}
            className="bg-white pl-9 pr-10 text-sm focus-visible:bg-white"
          />
          {searchValue && (
            <button
              type="button"
              onClick={handleClear}
              className={cn(
                "absolute right-2 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 transition-colors",
                "hover:bg-slate-100 hover:text-slate-600",
              )}
            >
              <X className="size-4" strokeWidth={2} />
              <span className="sr-only">Limpiar búsqueda</span>
            </button>
          )}
          {isTyping && (
            <Loader2 className="absolute right-10 top-1/2 size-4 -translate-y-1/2 animate-spin text-slate-400" />
          )}
        </div>
        <Button
          type="submit"
          size="sm"
          variant="outline"
          className="h-9 w-full border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50 sm:w-auto"
          disabled={isDisabled}
        >
          Buscar ahora
        </Button>
      </div>
    </form>
  );
};

export default TableSearch;
